/** Real application identity, organization context, and session controls. */
import {
	AppBar,
	Box,
	Button,
	IconButton,
	Stack,
	Toolbar,
	Tooltip,
	Typography,
	useMediaQuery,
	useTheme,
} from "@mui/material";
import MenuIcon from "@mui/icons-material/Menu";
import { Link as RouterLink } from "react-router-dom";

import { useAuth } from "../../auth";
import { BRAND_WORDMARK } from "../../theme/brand";
import { reducedMotionMediaQuery } from "../../theme/motionTokens";
import { usePageHeaderState } from "../PageHeaderContext";
import { AuthUserChip } from "./AuthUserChip";
import { FlahaLogo } from "./FlahaLogo";
import { TenantSwitcher } from "./TenantSwitcher";

interface TopAppBarProps {
	drawerWidth: number;
	onMenuClick: () => void;
	showMenuButton?: boolean;
	mobileMenuOpen?: boolean;
}

export function TopAppBar({
	drawerWidth,
	onMenuClick,
	showMenuButton = true,
	mobileMenuOpen = false,
}: TopAppBarProps) {
	const theme = useTheme();
	const isDesktop = useMediaQuery(theme.breakpoints.up("md"));
	const isPhone = useMediaQuery(theme.breakpoints.down("sm"));
	const header = usePageHeaderState();
	const { status } = useAuth();
	const isAuthed = status === "authenticated";

	return (
		<AppBar
			position="fixed"
			elevation={0}
			color="transparent"
			sx={{
				zIndex: theme.zIndex.drawer + 1,
				width: { md: `calc(100% - ${drawerWidth}px)` },
				ml: { md: `${drawerWidth}px` },
				color: "workspace.textPrimary",
				// Solid dark chrome (not milky glass) so logo marks stay crisp
				backgroundColor: "rgba(12, 16, 14, 0.96)",
				backgroundImage: "none",
				borderBottom: `1px solid ${theme.palette.workspace.border}`,
				boxShadow: "0 8px 24px rgba(0,0,0,0.22)",
				// Light blur only — heavy saturate was washing logos grey
				backdropFilter: "blur(10px)",
				WebkitBackdropFilter: "blur(10px)",
				transition: theme.motion.transition.surface,
				[reducedMotionMediaQuery]: { transition: "none" },
			}}
		>
			<Toolbar
				sx={{
					gap: { xs: 1, sm: 1.75 },
					minHeight: { xs: 64, sm: 72 },
					px: { xs: 1.25, sm: 2 },
					overflow: "hidden",
				}}
			>
				{!isDesktop && showMenuButton && (
					<Tooltip title="Open sidebar navigation">
						<IconButton
							color="inherit"
							edge="start"
							onClick={onMenuClick}
							aria-label="Open sidebar navigation"
							aria-controls="mobile-navigation"
							aria-expanded={mobileMenuOpen}
						>
							<MenuIcon />
						</IconButton>
					</Tooltip>
				)}

				<Box
					component={RouterLink}
					to={isAuthed ? "/dashboard" : "/"}
					aria-label={`${BRAND_WORDMARK} home`}
					sx={{
						flexShrink: 0,
						display: "flex",
						alignItems: "center",
						textDecoration: "none",
						color: "inherit",
						minWidth: 0,
						// Cap lockup width so project context still has room
						maxWidth: {
							xs: isPhone ? 48 : 200,
							sm: 240,
							md: 280,
						},
					}}
				>
					<FlahaLogo
						size={isPhone ? 36 : 40}
						variant={isPhone ? "mark" : "full"}
						color="#F6F0E3"
					/>
				</Box>

				{/* Context column — separated from brand so logos are not crowded */}
				<Box
					sx={{
						minWidth: 0,
						flex: 1,
						display: "flex",
						alignItems: "center",
						borderLeft: {
							sm: `1px solid ${theme.palette.workspace.border}`,
						},
						ml: { sm: 0.5 },
						pl: { sm: 1.75 },
					}}
				>
					{header.projectContext ? (
						<Stack spacing={0.15} sx={{ minWidth: 0 }}>
							<Typography
								variant="caption"
								sx={{
									color: "workspace.textMuted",
									lineHeight: 1,
									letterSpacing: "0.04em",
									textTransform: "uppercase",
									fontSize: 10,
								}}
							>
								Active project
							</Typography>
							<Typography
								variant="body2"
								noWrap
								sx={{ color: "workspace.textSecondary", fontWeight: 650 }}
							>
								{header.projectContext.name}
								{header.projectContext.code
									? ` · ${header.projectContext.code}`
									: ""}
							</Typography>
						</Stack>
					) : (
						header.title &&
						header.title !== BRAND_WORDMARK && (
							<Stack spacing={0.1} sx={{ minWidth: 0, display: { xs: "none", md: "flex" } }}>
								<Typography
									variant="body2"
									noWrap
									sx={{
										color: "workspace.textPrimary",
										fontWeight: 650,
										lineHeight: 1.2,
									}}
								>
									{header.title}
								</Typography>
								{header.subtitle && (
									<Typography
										variant="caption"
										noWrap
										sx={{ color: "workspace.textMuted", lineHeight: 1.2 }}
									>
										{header.subtitle}
									</Typography>
								)}
							</Stack>
						)
					)}
				</Box>

				{isAuthed && <TenantSwitcher compact={isPhone} />}

				{/* Public + authenticated: Pricing always reachable (sidebar hidden when logged out). */}
				{!isPhone && (
					<Button
						component={RouterLink}
						to="/pricing"
						color="inherit"
						size="small"
						sx={{
							flexShrink: 0,
							color: "workspace.textSecondary",
							fontWeight: 650,
							textTransform: "none",
							px: 1.25,
						}}
					>
						Pricing
					</Button>
				)}

				<Stack direction="row" alignItems="center" sx={{ flexShrink: 0 }}>
					<AuthUserChip />
				</Stack>
			</Toolbar>
		</AppBar>
	);
}
